'use strict';

const libPictProvider = require('pict-provider');

// The list is server-paged (a project can carry thousands of line items / equipment links), and server-side
// search narrows it before paging — so the tool never needs to load a whole long list into the browser.
const PAGE_SIZE = 100;      // rows fetched (and rendered) per page
const INN_CHUNK = 100;      // FK-batch size for name resolution
const CANDIDATE_CAP = 500;  // max related-name matches used to scope a join-entity search
const SELECT_ALL_CAP = 5000; // safety ceiling for "select all N matching" across pages

/**
 * ProjectBulkDelete — a reusable, project-scoped bulk-delete tool for the Project workspace's long lists
 * (line items, bid items, equipment links, personnel links, labels). Opened via openFor(pSpec) from a
 * per-tab "Delete…" button (gated by the `Project-BulkDelete` entitlement — see access/Task-Catalog.js).
 *
 * It renders its own overlay appended to <body> (so it never depends on, or is torn down by, the tab's own
 * rendering): a checkbox list of the project's rows with select-all, a live selected count, and a confirmed
 * "Delete selected" that removes each row through the API (soft-delete where the entity supports it; a hard
 * delete = unlink for the join tables). On close it calls pSpec.OnClose so the underlying tab refreshes.
 *
 * pSpec:
 *   Title, Noun, NounPlural       - human labels ("line item" / "line items")
 *   Verb                          - 'Delete' | 'Unlink' (wording only; the API call is the same deleteEntity)
 *   DeleteEntity                  - the Meadow entity to deleteEntity on (record entity, or the join entity)
 *   ItemsSet, IDField, Filter, Sort - server-paged list: `${ItemsSet}/FilteredTo/${Filter}[~FSF~${Sort}~ASC~0]/<offset>/PAGE`
 *   Columns: [ { Key, Label } ]   - display columns read off each row
 *   NameResolver?: { Set, IDField, NameField, FKField, Label } - for join rows, batch-resolve FK → name into a
 *                                   synthetic leading column (e.g. the Equipment/Contact name behind a join)
 *   SearchFields?: [ 'Field', … ] - DIRECT-field server search: the term is ANDed onto Filter as a LIKE stanza
 *                                   (via the picker's buildSearchFilter) so search + Count + paging stay accurate
 *   SearchResolver?: { Set, IDField, NameFields, JoinFKField } - JOIN-entity search: the term first resolves to a
 *                                   set of related ids (LIKE over NameFields on Set), then the join list is scoped
 *                                   `FBL~<JoinFKField>~INN~<ids>` — server search by a name the join row doesn't carry
 *   OnClose: fn                   - called after the overlay closes (refresh the tab)
 *
 * Handles arbitrarily long lists: only one PAGE_SIZE page is ever loaded/rendered, search filters server-side,
 * and selection persists across pages (keyed by row id) so a filtered batch can span pages before deleting.
 */
class ProjectBulkDeleteProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._spec = null;
		this._rows = [];           // the current page only
		this._total = 0;           // total matching the current filter (server Count)
		this._selected = {};       // rowID(string) -> true (persists across pages)
		this._loading = false;
		this._deleting = false;
		this._selecting = false;   // "select all matching" is walking the pages
		this._message = '';
		this._nameByFK = {};       // for NameResolver: FK value(string) -> resolved name
		this._hostSelector = '';   // the inline host on the active tab this tool renders into
		this._search = '';         // applied search term (server-side)
		this._searchDraft = '';    // what's typed in the box but not yet applied
		this._offset = 0;          // paging offset into the filtered set

		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			this.pict.CSSMap.addCSS('ProjectBulkDelete-CSS', _CSS, 540);
		}
	}

	get restClient() { return (this.pict.fable && this.pict.fable.HeadlightRestClient) || this.pict.HeadlightRestClient; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }

	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_deleteEntity(pEntity, pID) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.deleteEntity(pEntity, pID, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_chunk(pArray, pSize) { const tmpOut = []; for (let i = 0; i < pArray.length; i += pSize) { tmpOut.push(pArray.slice(i, i + pSize)); } return tmpOut; }

	// --- Open / close ---

	/**
	 * Open the selectable bulk-delete list INLINE, rendered into pSpec.Host (a selector on the active tab) —
	 * it replaces the tab's read list in place (no modal overlay). The host is (re)mounted by the view after
	 * every workspace paint, so openFor is safe to call repeatedly for the same spec.
	 */
	async openFor(pSpec)
	{
		if (!pSpec || !pSpec.Host) { return; }
		const tmpSameOpen = this._spec && (this._spec.Host === pSpec.Host) && (this._spec.Filter === pSpec.Filter);
		this._spec = pSpec;
		this._hostSelector = pSpec.Host;
		if (tmpSameOpen && (this._loaded || this._loading))
		{
			// The workspace repainted the host — re-render from cache, don't reload/lose the selection.
			this._render();
			return;
		}
		this._rows = [];
		this._total = 0;
		this._selected = {};
		this._deleting = false;
		this._message = '';
		this._nameByFK = {};
		this._search = '';
		this._searchDraft = '';
		this._offset = 0;
		this._loaded = false;
		this._loading = true;
		this._render();
		await this._loadAsync();
		this._loading = false;
		this._loaded = true;
		this._render();
	}

	/** Close the inline tool: clear the host and let the caller (OnClose) restore the tab's normal list. */
	close()
	{
		const tmpOnClose = this._spec && this._spec.OnClose;
		this._spec = null;
		this._loaded = false;
		this._rows = [];
		this._selected = {};
		if (typeof tmpOnClose === 'function') { try { tmpOnClose(); } catch (pError) { /* refresh is best-effort */ } }
	}

	// --- Load ---

	async _loadAsync()
	{
		const tmpSpec = this._spec;
		const tmpSort = tmpSpec.Sort ? `~FSF~${tmpSpec.Sort}~ASC~0` : '';
		const tmpEffective = await this._effectiveFilterAsync();
		this._nameByFK = {};
		if (tmpEffective.empty)
		{
			// A join-entity search matched no related records — nothing on this project can match.
			this._rows = [];
			this._total = 0;
			return;
		}
		const tmpListBase = `${tmpSpec.ItemsSet}/FilteredTo/${tmpEffective.filter}${tmpSort}`;
		const [ tmpRows, tmpCount ] = await Promise.all(
			[
				this._getJSON(`${tmpListBase}/${this._offset}/${PAGE_SIZE}`),
				this._getJSON(`${tmpSpec.ItemsSet}/Count/FilteredTo/${tmpEffective.filter}`),
			]);
		this._rows = Array.isArray(tmpRows) ? tmpRows : [];
		this._total = (tmpCount && (tmpCount.Count != null)) ? Number(tmpCount.Count) : this._rows.length;
		// Deletes may have emptied the page we were on — clamp to the last real page and refetch once.
		if ((this._rows.length === 0) && (this._offset > 0) && (this._total > 0))
		{
			this._offset = Math.max(0, (this._pageCount() - 1) * PAGE_SIZE);
			const tmpRefetch = await this._getJSON(`${tmpListBase}/${this._offset}/${PAGE_SIZE}`);
			this._rows = Array.isArray(tmpRefetch) ? tmpRefetch : [];
		}
		if (tmpSpec.NameResolver) { await this._resolveNames(); }
	}

	/**
	 * Build the effective FoxHound filter for the current search term: the spec's base Filter, plus (when a term is
	 * applied) either a direct-field LIKE stanza (SearchFields) or an id-scoping INN resolved from a related set
	 * (SearchResolver). Returns { filter, empty } — empty:true means a join search resolved to zero related records.
	 */
	async _effectiveFilterAsync()
	{
		const tmpSpec = this._spec;
		let tmpFilter = tmpSpec.Filter;
		const tmpTerm = String(this._search || '').trim();
		if (tmpTerm)
		{
			if (tmpSpec.SearchResolver)
			{
				const tmpIDs = await this._resolveSearchCandidatesAsync(tmpSpec.SearchResolver, tmpTerm);
				if (!tmpIDs || tmpIDs.length === 0) { return { filter: tmpFilter, empty: true }; }
				tmpFilter += `~FBL~${tmpSpec.SearchResolver.JoinFKField}~INN~${tmpIDs.slice(0, CANDIDATE_CAP).join(',')}`;
			}
			else if (tmpSpec.SearchFields && tmpSpec.SearchFields.length)
			{
				const tmpClause = this._searchClause(tmpSpec.SearchFields, tmpTerm);
				if (tmpClause) { tmpFilter += `~${tmpClause}`; }
			}
		}
		return { filter: tmpFilter, empty: false };
	}

	/** Direct-field search stanza via the picker's canonical builder (single field → LK; many → OR-group). */
	_searchClause(pFields, pTerm)
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (tmpPicker && (typeof tmpPicker.buildSearchFilter === 'function')) { return tmpPicker.buildSearchFilter(pFields, pTerm); }
		const tmpTerm = String(pTerm || '').trim();
		return tmpTerm ? `FBV~${pFields[0]}~LK~${encodeURIComponent(`%${tmpTerm}%`)}` : '';
	}

	/**
	 * Join-entity search: the join row carries only an FK, so resolve the term to a set of related ids by LIKE-ing
	 * each NameField on the related Set (parallel single-field queries merged — the grouped-OR isn't honored on
	 * every endpoint) and returning the matched ids to scope the join list with INN.
	 */
	async _resolveSearchCandidatesAsync(pResolver, pTerm)
	{
		const tmpFields = (pResolver.NameFields && pResolver.NameFields.length) ? pResolver.NameFields : ['Name'];
		const tmpLike = encodeURIComponent(`%${String(pTerm).trim()}%`);
		const tmpResults = await Promise.all(tmpFields.map((pField) =>
			this._getJSON(`${pResolver.Set}/FilteredTo/FBV~${pField}~LK~${tmpLike}/0/${CANDIDATE_CAP}`)));
		const tmpSeen = {};
		tmpResults.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pRec) =>
		{
			const tmpID = Number(pRec && pRec[pResolver.IDField]);
			if (tmpID > 0) { tmpSeen[tmpID] = true; }
		}));
		return Object.keys(tmpSeen).map(Number);
	}

	/** For join specs: batch-resolve the FK → display name (chunked INN), stashed in _nameByFK. */
	async _resolveNames()
	{
		const tmpNR = this._spec.NameResolver;
		const tmpIDs = Array.from(new Set(this._rows.map((pRow) => Number(pRow[tmpNR.FKField])).filter((pV) => pV > 0)));
		if (tmpIDs.length === 0) { return; }
		const tmpChunks = this._chunk(tmpIDs, INN_CHUNK);
		const tmpResults = await Promise.all(tmpChunks.map((pChunk) =>
			this._getJSON(`${tmpNR.Set}/FilteredTo/FBL~${tmpNR.IDField}~INN~${pChunk.join(',')}/0/${INN_CHUNK}`)));
		tmpResults.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pRec) =>
		{
			if (pRec && pRec[tmpNR.IDField] != null) { this._nameByFK[String(pRec[tmpNR.IDField])] = pRec[tmpNR.NameField] || `#${pRec[tmpNR.IDField]}`; }
		}));
	}

	// --- Selection ---

	_selectedIDs() { return Object.keys(this._selected).filter((pK) => this._selected[pK]); }
	selectedCount() { return this._selectedIDs().length; }

	toggleRow(pID)
	{
		const tmpKey = String(pID);
		if (this._selected[tmpKey]) { delete this._selected[tmpKey]; } else { this._selected[tmpKey] = true; }
		this._syncSelectionUI(tmpKey);
	}

	/** True when every row on the CURRENT page is selected (selection can also span other pages). */
	_pageAllSelected() { return (this._rows.length > 0) && this._rows.every((pRow) => !!this._selected[String(pRow[this._spec.IDField])]); }

	/** Header checkbox: toggle only the current page's rows, leaving selections on other pages intact. */
	toggleSelectAll()
	{
		const tmpAll = this._pageAllSelected();
		this._rows.forEach((pRow) =>
		{
			const tmpKey = String(pRow[this._spec.IDField]);
			if (tmpAll) { delete this._selected[tmpKey]; } else { this._selected[tmpKey] = true; }
		});
		this._render();
	}

	/** Walk every page of the current filter and select all matching ids (capped) — for deleting a large filtered set. */
	async selectAllMatchingAsync()
	{
		if (this._loading || this._deleting || this._selecting) { return; }
		const tmpSpec = this._spec;
		const tmpEffective = await this._effectiveFilterAsync();
		if (tmpEffective.empty) { return; }
		const tmpSort = tmpSpec.Sort ? `~FSF~${tmpSpec.Sort}~ASC~0` : '';
		const tmpBase = `${tmpSpec.ItemsSet}/FilteredTo/${tmpEffective.filter}${tmpSort}`;
		this._selecting = true;
		this._message = '';
		this._render();
		let tmpOffset = 0;
		let tmpGuard = 0;
		while ((tmpOffset < this._total) && (tmpOffset < SELECT_ALL_CAP) && (tmpGuard < 200))
		{
			tmpGuard++;
			const tmpRows = await this._getJSON(`${tmpBase}/${tmpOffset}/${PAGE_SIZE}`);
			const tmpList = Array.isArray(tmpRows) ? tmpRows : [];
			if (tmpList.length === 0) { break; }
			tmpList.forEach((pRow) => { this._selected[String(pRow[tmpSpec.IDField])] = true; });
			tmpOffset += PAGE_SIZE;
		}
		this._selecting = false;
		if (this._total > SELECT_ALL_CAP) { this._message = `Selected the first ${SELECT_ALL_CAP.toLocaleString()} of ${this._total.toLocaleString()} — ${tmpSpec.Verb === 'Unlink' ? 'unlink' : 'delete'} these, then select more.`; }
		this._render();
	}

	// --- Paging ---

	_pageCount() { return Math.max(1, Math.ceil(this._total / PAGE_SIZE)); }
	_currentPage() { return Math.floor(this._offset / PAGE_SIZE); }

	async gotoPageAsync(pDelta)
	{
		if (this._loading || this._deleting || this._selecting) { return; }
		const tmpMax = (this._pageCount() - 1) * PAGE_SIZE;
		let tmpNext = this._offset + (Number(pDelta) * PAGE_SIZE);
		if (tmpNext < 0) { tmpNext = 0; }
		if (tmpNext > tmpMax) { tmpNext = tmpMax; }
		if (tmpNext === this._offset) { return; }
		this._offset = tmpNext;
		this._loading = true;
		this._render();
		await this._loadAsync();
		this._loading = false;
		this._render();
	}

	// --- Search ---

	/** Track the input value without re-rendering (so the field keeps focus while typing). */
	setSearchDraft(pValue) { this._searchDraft = String(pValue == null ? '' : pValue); }

	async applySearchAsync()
	{
		if (this._loading || this._deleting || this._selecting) { return; }
		const tmpTerm = String(this._searchDraft || '').trim();
		if (tmpTerm === this._search) { return; }
		this._search = tmpTerm;
		this._offset = 0;
		this._message = '';
		this._loading = true;
		this._render();
		await this._loadAsync();
		this._loading = false;
		this._render();
	}

	async clearSearchAsync()
	{
		if (!this._search && !this._searchDraft) { return; }
		this._search = '';
		this._searchDraft = '';
		this._offset = 0;
		this._message = '';
		this._loading = true;
		this._render();
		await this._loadAsync();
		this._loading = false;
		this._render();
	}

	/** Light per-row selection repaint (row class + toolbar count) without rebuilding the whole table. */
	_syncSelectionUI(pID)
	{
		if (typeof document === 'undefined') { return; }
		const tmpRow = document.getElementById(`pbd-row-${pID}`);
		if (tmpRow) { tmpRow.classList.toggle('pbd-row-sel', !!this._selected[String(pID)]); }
		const tmpBar = document.getElementById('pbd-toolbar');
		if (tmpBar) { tmpBar.innerHTML = this._toolbarHTML(); }
	}

	// --- Delete ---

	async deleteSelectedAsync()
	{
		if (this._deleting) { return; }
		const tmpIDs = this._selectedIDs();
		if (tmpIDs.length === 0) { return; }
		const tmpSpec = this._spec;
		const tmpVerb = (tmpSpec.Verb === 'Unlink') ? 'unlink' : 'delete';
		const tmpNoun = (tmpIDs.length === 1) ? tmpSpec.Noun : tmpSpec.NounPlural;
		const tmpConfirmText = `${tmpVerb === 'unlink' ? 'Unlink' : 'Delete'} ${tmpIDs.length} ${tmpNoun}? ${tmpVerb === 'unlink' ? 'The shared records stay; only their link to this project is removed.' : 'This can be undone by an administrator.'}`;
		const tmpOK = (this.modal && (typeof this.modal.confirm === 'function'))
			? await this.modal.confirm(tmpConfirmText, { title: `${tmpVerb === 'unlink' ? 'Unlink' : 'Delete'} ${tmpNoun}`, confirmLabel: (tmpVerb === 'unlink' ? 'Unlink' : 'Delete'), cancelLabel: 'Cancel', dangerous: true })
			: (typeof window !== 'undefined' ? window.confirm(tmpConfirmText) : false);
		if (!tmpOK) { return; }

		this._deleting = true;
		this._message = '';
		this._render();
		let tmpDone = 0;
		let tmpFail = 0;
		for (let i = 0; i < tmpIDs.length; i++)
		{
			const tmpRes = await this._deleteEntity(tmpSpec.DeleteEntity, tmpIDs[i]);
			if (tmpRes && !tmpRes.error && !(tmpRes.result && tmpRes.result.ErrorCode)) { tmpDone++; delete this._selected[tmpIDs[i]]; }
			else { tmpFail++; }
		}
		this._deleting = false;
		this._message = `${tmpVerb === 'unlink' ? 'Unlinked' : 'Deleted'} ${tmpDone} ${tmpDone === 1 ? tmpSpec.Noun : tmpSpec.NounPlural}${tmpFail ? `, ${tmpFail} failed (you may lack permission)` : ''}.`;
		// Reload the remaining rows from the server so the list reflects the deletions.
		this._loading = true;
		this._render();
		await this._loadAsync();
		this._loading = false;
		this._render();
	}

	// --- Render (inline into the active tab's host) ---

	_render()
	{
		if (!this._spec || !this._hostSelector) { return; }
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || (tmpCA.getElement(this._hostSelector).length === 0)) { return; }
		tmpCA.assignContent(this._hostSelector, this._panelHTML());
	}

	_panelHTML()
	{
		const tmpSpec = this._spec;
		let tmpBody;
		if (this._loading) { tmpBody = `<div class="pbd-empty">Loading ${this._esc(tmpSpec.NounPlural)}…</div>`; }
		else if (this._rows.length === 0)
		{
			tmpBody = this._search
				? `<div class="pbd-empty">No ${this._esc(tmpSpec.NounPlural)} match “${this._esc(this._search)}”.</div>`
				: `<div class="pbd-empty">This project has no ${this._esc(tmpSpec.NounPlural)}.</div>`;
		}
		else { tmpBody = this._tableHTML(); }
		const tmpMsg = this._message ? `<div class="pbd-msg">${this._esc(this._message)}</div>` : '';
		return `<div class="pbd-panel">
			<div class="pbd-head">
				<span class="pbd-title">${this._esc(tmpSpec.Title)}</span>
				<button type="button" class="pbd-btn pbd-cancel" onclick="_Pict.providers.ProjectBulkDelete.close()">Done</button>
			</div>
			${this._searchBarHTML()}
			<div id="pbd-toolbar" class="pbd-toolbar">${this._toolbarHTML()}</div>
			${this._pagerHTML()}
			${tmpMsg}
			<div class="pbd-tablewrap">${tmpBody}</div>
		</div>`;
	}

	_searchBarHTML()
	{
		const tmpSpec = this._spec || {};
		if (!tmpSpec.SearchFields && !tmpSpec.SearchResolver) { return ''; }
		const tmpHasSearch = !!this._search;
		return `<div class="pbd-search">
			<input type="text" class="pbd-search-input" placeholder="Search ${this._esc(tmpSpec.NounPlural)}…" value="${this._esc(this._searchDraft)}"
				oninput="_Pict.providers.ProjectBulkDelete.setSearchDraft(this.value)"
				onkeydown="if(event.key==='Enter'){event.preventDefault();_Pict.providers.ProjectBulkDelete.applySearchAsync();}">
			<button type="button" class="pbd-btn" onclick="_Pict.providers.ProjectBulkDelete.applySearchAsync()">Search</button>
			${tmpHasSearch ? `<button type="button" class="pbd-btn pbd-search-clear" onclick="_Pict.providers.ProjectBulkDelete.clearSearchAsync()">Clear</button>` : ''}
		</div>`;
	}

	_pagerHTML()
	{
		if (this._loading || (this._rows.length === 0)) { return ''; }
		const tmpTotal = this._total;
		const tmpFrom = (tmpTotal === 0) ? 0 : (this._offset + 1);
		const tmpTo = Math.min(this._offset + this._rows.length, tmpTotal);
		const tmpBusy = this._deleting || this._selecting;
		const tmpPrevDis = ((this._offset <= 0) || tmpBusy) ? 'disabled' : '';
		const tmpNextDis = (((this._offset + PAGE_SIZE) >= tmpTotal) || tmpBusy) ? 'disabled' : '';
		const tmpMore = (tmpTotal > this._rows.length);
		const tmpSelAll = (tmpMore && !tmpBusy)
			? `<button type="button" class="pbd-linkbtn" onclick="_Pict.providers.ProjectBulkDelete.selectAllMatchingAsync()">Select all ${tmpTotal.toLocaleString()} matching</button>`
			: (this._selecting ? `<span class="pbd-selecting">Selecting…</span>` : '');
		const tmpPageInd = tmpMore ? `<span class="pbd-pageind">Page ${this._currentPage() + 1} of ${this._pageCount()}</span>` : '';
		return `<div class="pbd-pager">
			<span class="pbd-range">${tmpFrom.toLocaleString()}–${tmpTo.toLocaleString()} of ${tmpTotal.toLocaleString()}</span>
			${tmpSelAll}
			<span class="pbd-pagerbtns">${tmpPageInd}
				<button type="button" class="pbd-btn" ${tmpPrevDis} onclick="_Pict.providers.ProjectBulkDelete.gotoPageAsync(-1)">‹ Prev</button>
				<button type="button" class="pbd-btn" ${tmpNextDis} onclick="_Pict.providers.ProjectBulkDelete.gotoPageAsync(1)">Next ›</button>
			</span>
		</div>`;
	}

	_toolbarHTML()
	{
		const tmpSpec = this._spec || {};
		const tmpSel = this.selectedCount();
		const tmpVerbCap = (tmpSpec.Verb === 'Unlink') ? 'Unlink' : 'Delete';
		const tmpDisabled = (tmpSel === 0 || this._deleting || this._selecting) ? 'disabled' : '';
		const tmpLabel = this._deleting ? 'Working…' : `${tmpVerbCap} selected${tmpSel ? ` (${tmpSel})` : ''}`;
		return `<span class="pbd-count">${tmpSel.toLocaleString()} selected</span>
			<button type="button" class="pbd-btn pbd-btn-danger" ${tmpDisabled} onclick="_Pict.providers.ProjectBulkDelete.deleteSelectedAsync()">${tmpLabel}</button>`;
	}

	_tableHTML()
	{
		const tmpSpec = this._spec;
		const tmpAllSelected = this._pageAllSelected();
		const tmpNameCol = tmpSpec.NameResolver ? `<th>${this._esc(tmpSpec.NameResolver.Label || 'Name')}</th>` : '';
		const tmpCols = (tmpSpec.Columns || []).map((pCol) => `<th>${this._esc(pCol.Label)}</th>`).join('');
		const tmpRows = this._rows.map((pRow) =>
		{
			const tmpID = pRow[tmpSpec.IDField];
			const tmpSel = !!this._selected[String(tmpID)];
			const tmpNameCell = tmpSpec.NameResolver
				? `<td>${this._esc(this._nameByFK[String(pRow[tmpSpec.NameResolver.FKField])] || `#${pRow[tmpSpec.NameResolver.FKField]}`)}</td>`
				: '';
			const tmpCells = (tmpSpec.Columns || []).map((pCol) => `<td>${this._esc(pRow[pCol.Key] != null && String(pRow[pCol.Key]).trim() !== '' ? pRow[pCol.Key] : '—')}</td>`).join('');
			return `<tr id="pbd-row-${tmpID}" class="pbd-row${tmpSel ? ' pbd-row-sel' : ''}" onclick="_Pict.providers.ProjectBulkDelete.toggleRow('${tmpID}')">
				<td class="pbd-check"><span class="pbd-checkbox${tmpSel ? ' pbd-checkbox-on' : ''}">${tmpSel ? '✓' : ''}</span></td>${tmpNameCell}${tmpCells}</tr>`;
		}).join('');
		return `<table class="pbd-table">
			<thead><tr><th class="pbd-check" title="Select all on this page" onclick="_Pict.providers.ProjectBulkDelete.toggleSelectAll()"><span class="pbd-checkbox${tmpAllSelected ? ' pbd-checkbox-on' : ''}">${tmpAllSelected ? '✓' : ''}</span></th>${tmpNameCol}${tmpCols}</tr></thead>
			<tbody>${tmpRows}</tbody></table>`;
	}
}

module.exports = ProjectBulkDeleteProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ProjectBulkDelete',
	AutoInitialize: true,
	AutoInitializeOrdinal: 225,
};

const _CSS = /*css*/`
	.pbd-panel { display: flex; flex-direction: column; border: 1px solid var(--theme-color-status-error, #cf5b5b); border-radius: 10px; overflow: hidden; background: var(--theme-color-background-panel, #fff); }
	.pbd-head { display: flex; align-items: center; justify-content: space-between; padding: 0.7rem 1.1rem; background: color-mix(in srgb, var(--theme-color-status-error, #cf5b5b) 8%, transparent); border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
	.pbd-title { font-weight: 700; font-size: 0.98rem; color: var(--theme-color-text-primary, #1f2733); }
	.pbd-cancel { font-weight: 600; }
	.pbd-search { display: flex; align-items: center; gap: 0.55rem; padding: 0.7rem 1.1rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
	.pbd-search-input { flex: 1 1 auto; min-width: 0; font-size: 0.88rem; padding: 0.42rem 0.7rem; border-radius: 7px; border: 1px solid var(--theme-color-border-medium, #d5dbe3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); }
	.pbd-search-input:focus { outline: none; border-color: var(--theme-color-primary, #3f6fd1); box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color-primary, #3f6fd1) 22%, transparent); }
	.pbd-toolbar { display: flex; align-items: center; gap: 0.9rem; padding: 0.7rem 1.1rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
	.pbd-count { font-size: 0.85rem; font-weight: 600; color: var(--theme-color-text-secondary, #45505f); margin-right: auto; }
	.pbd-pager { display: flex; align-items: center; gap: 0.9rem; padding: 0.5rem 1.1rem; background: var(--theme-color-background-tertiary, #f4f6f9); border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
	.pbd-range { font-size: 0.8rem; color: var(--theme-color-text-secondary, #45505f); }
	.pbd-pagerbtns { display: flex; align-items: center; gap: 0.5rem; margin-left: auto; }
	.pbd-pageind { font-size: 0.78rem; color: var(--theme-color-text-muted, #6b7686); }
	.pbd-linkbtn { font-size: 0.8rem; font-weight: 600; padding: 0; border: 0; background: none; color: var(--theme-color-primary, #3f6fd1); cursor: pointer; text-decoration: underline; }
	.pbd-selecting { font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); }
	.pbd-btn { font-size: 0.85rem; font-weight: 600; padding: 0.42rem 0.95rem; border-radius: 7px; border: 1px solid var(--theme-color-border-medium, #d5dbe3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); cursor: pointer; }
	.pbd-btn[disabled] { opacity: 0.5; cursor: default; }
	.pbd-btn-danger { color: #fff; background: var(--theme-color-status-error, #cf5b5b); border-color: var(--theme-color-status-error, #cf5b5b); }
	.pbd-note { font-size: 0.8rem; color: #8a6d1b; background: var(--theme-color-status-warning-bg, #fbf3d9); padding: 0.45rem 1.1rem; }
	.pbd-msg { font-size: 0.84rem; color: var(--theme-color-text-secondary, #45505f); background: var(--theme-color-background-tertiary, #f4f6f9); padding: 0.45rem 1.1rem; }
	.pbd-empty { padding: 2rem 1.1rem; text-align: center; color: var(--theme-color-text-muted, #6b7686); }
	.pbd-tablewrap { overflow-x: auto; }
	.pbd-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.88rem; }
	.pbd-table th { position: sticky; top: 0; text-align: left; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); background: var(--theme-color-background-tertiary, #f4f6f9); padding: 0.5rem 0.85rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
	.pbd-table td { padding: 0.5rem 0.85rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); color: var(--theme-color-text-primary, #1f2733); }
	.pbd-row { cursor: pointer; }
	.pbd-row:hover { background: var(--theme-color-background-tertiary, #f4f6f9); }
	.pbd-row-sel { background: color-mix(in srgb, var(--theme-color-status-error, #cf5b5b) 10%, transparent); }
	.pbd-check { width: 1%; white-space: nowrap; text-align: center; }
	.pbd-checkbox { display: inline-flex; align-items: center; justify-content: center; width: 1.05rem; height: 1.05rem; border: 1.5px solid var(--theme-color-border-medium, #d5dbe3); border-radius: 4px; font-size: 0.72rem; font-weight: 800; color: #fff; background: var(--theme-color-background-panel, #fff); }
	.pbd-checkbox-on { background: var(--theme-color-status-error, #cf5b5b); border-color: var(--theme-color-status-error, #cf5b5b); }
	.pbd-check th, th.pbd-check { cursor: pointer; }
`;
